import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  CrosswordGrid,
  CrosswordWord,
  CROSSWORD_GRIDS,
  buildGridMatrix,
  getWordCells,
} from '../data/crosswordData';
import { playSound, triggerHaptic } from '../audio';
import { TTSButton } from './TTSButton';
import { useKabyleTTS } from '../hooks/useKabyleTTS';
import { BerberRugBorder, BerberRugDivider } from './Sprites';
import { CrosswordGridCell, CrosswordVirtualKeyboard } from './CrosswordComponents';

interface CrosswordChallengeViewProps {
  score?: number;
  setScore?: React.Dispatch<React.SetStateAction<number>>;
  onBack: () => void;
  unlockedLevels?: number[];
  onPlayingChange?: (isPlaying: boolean) => void;
  challengeGrid?: CrosswordGrid | null;
  challengeGrids?: CrosswordGrid[];
  challengeTitle?: string;
  onAddScore?: (xp: number) => void;
}

const STORAGE_SOLVED_KEY = 'tamazight_crosswords_solved_grids';

/**
 * Normalizes Tamazight Latin characters by removing sub-dots (., emphatics)
 * and carons (^ inversé), as well as standard accents.
 * E.g., Č -> C, Ǧ -> G, Ž -> Z, Ḍ -> D, Ḥ -> H, Ṛ -> R, Ṣ -> S, Ṭ -> T, Ẓ -> Z, Ɛ -> E, Ɣ -> G
 */
export const normalizeTamazightChar = (char: string): string => {
  if (!char) return '';
  const upper = char.toUpperCase().trim();
  switch (upper) {
    case 'Č': return 'C';
    case 'Ǧ': return 'G';
    case 'Ž': return 'Z';
    case 'Ḍ': return 'D';
    case 'Ḥ': return 'H';
    case 'Ṛ': return 'R';
    case 'Ṣ': return 'S';
    case 'Ṭ': return 'T';
    case 'Ẓ': return 'Z';
    case 'Ɛ': return 'E';
    case 'Ɣ': return 'G';
    default:
      // Strip any combining marks (dots below, carons, diacritics)
      return upper.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }
};

/**
 * Checks if an input character matches the target character, either exactly
 * or via simplified Latin form (without sub-dot . or caron ^ inversé).
 */
export const tamazightCharMatches = (input: string, target: string): boolean => {
  if (!input || !target) return false;
  const inUp = input.toUpperCase().trim();
  const tarUp = target.toUpperCase().trim();
  if (inUp === tarUp) return true;
  return normalizeTamazightChar(inUp) === normalizeTamazightChar(tarUp);
};

export const CrosswordChallengeView: React.FC<CrosswordChallengeViewProps> = ({
  score = 0,
  setScore,
  onBack,
  unlockedLevels = [0, 1, 2, 3, 4],
  onPlayingChange,
  challengeGrid,
  challengeGrids,
  challengeTitle,
  onAddScore,
}) => {
  // Currently selected grid (defaults to challengeGrid or first challengeGrids if provided)
  const [selectedGrid, setSelectedGrid] = useState<CrosswordGrid | null>(
    () => challengeGrid || (challengeGrids && challengeGrids.length > 0 ? challengeGrids[0] : null)
  );

  // Solved grids set
  const [solvedGridIds, setSolvedGridIds] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_SOLVED_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // User input grid letters per grid: { [`${row}_${col}`]: 'A' }
  const [userLetters, setUserLetters] = useState<Record<string, string>>(() => {
    const initialGrid = challengeGrid || (challengeGrids && challengeGrids.length > 0 ? challengeGrids[0] : null);
    if (initialGrid) {
      let letters: Record<string, string> = {};
      try {
        const raw = localStorage.getItem(`tamazight_crossword_letters_${initialGrid.id}`);
        if (raw) letters = JSON.parse(raw);
      } catch {}

      // Auto-heal complete letters if grid was already registered as solved
      try {
        const savedSolved = localStorage.getItem(STORAGE_SOLVED_KEY);
        const solvedList: string[] = savedSolved ? JSON.parse(savedSolved) : [];
        if (solvedList.includes(initialGrid.id)) {
          initialGrid.words.forEach((w) => {
            const cells = getWordCells(w);
            cells.forEach((c) => {
              if (!c.isSpace) {
                letters[`${c.row}_${c.col}`] = c.char;
              }
            });
          });
        }
      } catch {}

      return letters;
    }
    return {};
  });

  // Progressive/Evolving grid manual reveal counter
  const [manualRevealedCount, setManualRevealedCount] = useState<number>(() => {
    const initialGrid = challengeGrid || (challengeGrids && challengeGrids.length > 0 ? challengeGrids[0] : null);
    if (initialGrid) {
      try {
        const raw = localStorage.getItem(`tamazight_crossword_revealed_${initialGrid.id}`);
        if (raw) return parseInt(raw, 10) || 1;
      } catch {}
    }
    return 1;
  });

  // Number of revealed letters per word (capped strictly at 50% of word length)
  const [revealedCountPerWord, setRevealedCountPerWord] = useState<Record<string, number>>({});

  // Set of cell keys `${r}_${c}` that were individually revealed with the clue feature
  const [revealedCellKeys, setRevealedCellKeys] = useState<string[]>(() => {
    const initialGrid = challengeGrid || (challengeGrids && challengeGrids.length > 0 ? challengeGrids[0] : null);
    if (initialGrid) {
      try {
        const raw = localStorage.getItem(`tamazight_crossword_revealed_cells_${initialGrid.id}`);
        if (raw) return JSON.parse(raw);
      } catch {}
    }
    return [];
  });

  // Active word and active cell
  const [activeWordId, setActiveWordId] = useState<string | null>(null);
   const [activeCell, setActiveCell] = useState<{ row: number; col: number } | null>(null);

  // Ref for active cell and grid container to handle auto-scrolling, drag-to-pan and scrollable hints
  const activeCellRef = useRef<HTMLButtonElement | null>(null);
  const gridContainerRef = useRef<HTMLDivElement | null>(null);
  const [showScrollHint, setShowScrollHint] = useState<boolean>(false);
  const toastTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Persistent displacement position tracking per grid (to prevent resets on re-render / typing)
  const savedDisplacementRef = useRef<{ left: number; top: number }>({ left: 0, top: 0 });
  const hasUserDisplacedRef = useRef<boolean>(false);
  const isRestoringScrollRef = useRef<boolean>(false);
  const saveScrollTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Mouse / Pointer drag-to-pan states
  const isDraggingRef = useRef<boolean>(false);
  const dragStartRef = useRef<{ x: number; y: number; scrollLeft: number; scrollTop: number }>({
    x: 0,
    y: 0,
    scrollLeft: 0,
    scrollTop: 0,
  });
  const hasDraggedRef = useRef<boolean>(false);

  // Modals & Panels
  const [showVictoryModal, setShowVictoryModal] = useState<boolean>(false);
  const [showHintsModal, setShowHintsModal] = useState<boolean>(false);
  const [showAllCluesModal, setShowAllCluesModal] = useState<boolean>(false);
  const [gridCompletedJustNow, setGridCompletedJustNow] = useState<boolean>(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  const { playTTS } = useKabyleTTS();

  const showToast = useCallback((msg: string) => {
    // Immediately clear scroll hint so overlays never overlap
    setShowScrollHint(false);
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    setToastMsg(msg);
    toastTimerRef.current = setTimeout(() => {
      setToastMsg(null);
    }, 2800);
  }, []);

  const shownGridHintIdsRef = useRef<Set<string>>(new Set());
  const userLettersRef = useRef<Record<string, string>>({});
  userLettersRef.current = userLetters;
  const selectedGridRef = useRef<CrosswordGrid | null>(selectedGrid);
  selectedGridRef.current = selectedGrid;

  // Checks if a crossword grid is empty (no letters entered, no clues revealed)
  const isGridEmpty = useCallback((gridId: string, currentLetters?: Record<string, string>): boolean => {
    try {
      const letters = currentLetters !== undefined ? currentLetters : userLettersRef.current;
      if (letters && typeof letters === 'object') {
        const hasLetters = Object.values(letters).some((val) => typeof val === 'string' && val.trim().length > 0);
        if (hasLetters) return false;
      }
      const lettersStr = localStorage.getItem(`tamazight_crossword_letters_${gridId}`);
      if (lettersStr) {
        const lettersObj = JSON.parse(lettersStr);
        if (lettersObj && typeof lettersObj === 'object') {
          const hasLetters = Object.values(lettersObj).some((val) => typeof val === 'string' && val.trim().length > 0);
          if (hasLetters) return false;
        }
      }
      const revCellsStr = localStorage.getItem(`tamazight_crossword_revealed_cells_${gridId}`);
      if (revCellsStr) {
        const revCellsList = JSON.parse(revCellsStr);
        if (Array.isArray(revCellsList) && revCellsList.length > 0) return false;
      }
    } catch {}
    return true;
  }, []);

  const triggerScrollHint = useCallback((force = false) => {
    // Prevent superposition if victory modal or other full modals are open
    if (showVictoryModal || showHintsModal || showAllCluesModal) return;

    if (!force && selectedGridRef.current && !isGridEmpty(selectedGridRef.current.id)) return;

    setShowScrollHint(true);
  }, [showVictoryModal, showHintsModal, showAllCluesModal, isGridEmpty]);

  // Trigger Apple-style gesture coaching overlay on grid opening if empty / not started
  useEffect(() => {
    if (!selectedGrid) return;
    const gridId = selectedGrid.id;
    if (!shownGridHintIdsRef.current.has(gridId)) {
      if (isGridEmpty(gridId)) {
        const timer = setTimeout(() => {
          if (!shownGridHintIdsRef.current.has(gridId)) {
            shownGridHintIdsRef.current.add(gridId);
            triggerScrollHint(true);
          }
        }, 300);
        return () => clearTimeout(timer);
      } else {
        shownGridHintIdsRef.current.add(gridId);
      }
    }
  }, [selectedGrid?.id, triggerScrollHint, isGridEmpty]);

  // Restore saved active word and active cell from localStorage
  const restoreSavedWordAndCell = useCallback((grid: CrosswordGrid): boolean => {
    try {
      const savedWordId = localStorage.getItem(`tamazight_crossword_active_word_${grid.id}`);
      const savedCellRaw = localStorage.getItem(`tamazight_crossword_active_cell_${grid.id}`);
      if (savedWordId && grid.words.some((w) => w.id === savedWordId)) {
        setActiveWordId(savedWordId);
        if (savedCellRaw) {
          const parsedCell = JSON.parse(savedCellRaw);
          if (typeof parsedCell?.row === 'number' && typeof parsedCell?.col === 'number') {
            setActiveCell(parsedCell);
            return true;
          }
        }
        const word = grid.words.find((w) => w.id === savedWordId);
        if (word) {
          setActiveCell({ row: word.row, col: word.col });
          return true;
        }
      }
    } catch {}
    return false;
  }, []);

  // Restore player displacement scroll position for a grid
  const restoreGridPosition = useCallback((gridId: string) => {
    try {
      const raw = localStorage.getItem(`tamazight_crossword_scroll_${gridId}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (typeof parsed?.left === 'number' && typeof parsed?.top === 'number') {
          savedDisplacementRef.current = { left: parsed.left, top: parsed.top };
          hasUserDisplacedRef.current = true;
          isRestoringScrollRef.current = true;
          requestAnimationFrame(() => {
            if (gridContainerRef.current) {
              gridContainerRef.current.scrollLeft = parsed.left;
              gridContainerRef.current.scrollTop = parsed.top;
            }
            setTimeout(() => {
              if (gridContainerRef.current) {
                gridContainerRef.current.scrollLeft = parsed.left;
                gridContainerRef.current.scrollTop = parsed.top;
              }
              isRestoringScrollRef.current = false;
            }, 60);
          });
          return;
        }
      }
    } catch {}
    savedDisplacementRef.current = { left: 0, top: 0 };
    hasUserDisplacedRef.current = false;
  }, []);

  // Handle scroll events on grid container to track player displacement
  const handleContainerScroll = useCallback(() => {
    if (isRestoringScrollRef.current) return;
    const container = gridContainerRef.current;
    if (!container || !selectedGridRef.current) return;

    savedDisplacementRef.current = {
      left: container.scrollLeft,
      top: container.scrollTop,
    };
    hasUserDisplacedRef.current = true;

    if (saveScrollTimerRef.current) clearTimeout(saveScrollTimerRef.current);
    saveScrollTimerRef.current = setTimeout(() => {
      const curGrid = selectedGridRef.current;
      if (curGrid) {
        try {
          localStorage.setItem(
            `tamazight_crossword_scroll_${curGrid.id}`,
            JSON.stringify(savedDisplacementRef.current)
          );
        } catch {}
      }
    }, 120);
  }, []);

  // Drag-to-pan handlers for mouse/touch navigation across the grid
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (e.button !== 0) return;
    const container = gridContainerRef.current;
    if (!container) return;

    isDraggingRef.current = true;
    hasDraggedRef.current = false;
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      scrollLeft: container.scrollLeft,
      scrollTop: container.scrollTop,
    };
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDraggingRef.current) return;
    const container = gridContainerRef.current;
    if (!container) return;

    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;

    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
      hasDraggedRef.current = true;
      hasUserDisplacedRef.current = true;
      container.scrollLeft = dragStartRef.current.scrollLeft - dx;
      container.scrollTop = dragStartRef.current.scrollTop - dy;
      savedDisplacementRef.current = {
        left: container.scrollLeft,
        top: container.scrollTop,
      };
    }
  }, []);

  const handlePointerUp = useCallback(() => {
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
      if (hasDraggedRef.current && selectedGridRef.current) {
        try {
          localStorage.setItem(
            `tamazight_crossword_scroll_${selectedGridRef.current.id}`,
            JSON.stringify(savedDisplacementRef.current)
          );
        } catch {}
      }
    }
  }, []);

  const prevChallengeGridIdRef = useRef<string | null>(null);

  useEffect(() => {
    const target = challengeGrid || (challengeGrids && challengeGrids.length > 0 ? challengeGrids[0] : null);
    if (target) {
      if (prevChallengeGridIdRef.current === target.id) return;
      prevChallengeGridIdRef.current = target.id;

      setSelectedGrid(target);
      try {
        const rawLetters = localStorage.getItem(`tamazight_crossword_letters_${target.id}`);
        if (rawLetters) {
          setUserLetters(JSON.parse(rawLetters));
        } else {
          setUserLetters({});
        }
        const rawRev = localStorage.getItem(`tamazight_crossword_revealed_${target.id}`);
        if (rawRev) setManualRevealedCount(parseInt(rawRev, 10) || 1);
        const rawRevCells = localStorage.getItem(`tamazight_crossword_revealed_cells_${target.id}`);
        if (rawRevCells) {
          setRevealedCellKeys(JSON.parse(rawRevCells));
        } else {
          setRevealedCellKeys([]);
        }
      } catch {
        setUserLetters({});
        setRevealedCellKeys([]);
      }

      // Try to restore saved active word & cell
      const restored = restoreSavedWordAndCell(target);
      if (!restored && target.words.length > 0) {
        const firstWord = target.words[0];
        setActiveWordId(firstWord.id);
        setActiveCell({ row: firstWord.row, col: firstWord.col });
      }

      // Restore saved displacement position
      restoreGridPosition(target.id);
    }
  }, [challengeGrid?.id, challengeGrids, restoreSavedWordAndCell, restoreGridPosition]);

  const onPlayingChangeRef = useRef(onPlayingChange);
  onPlayingChangeRef.current = onPlayingChange;

  // Notify parent component about playing state
  useEffect(() => {
    onPlayingChangeRef.current?.(selectedGrid !== null);
  }, [selectedGrid !== null]);

  // Fast memoized Set of solved word IDs (accepts standard Latin or special Tamazight letters)
  const solvedWordIds = useMemo(() => {
    if (!selectedGrid) return new Set<string>();
    const set = new Set<string>();
    for (const word of selectedGrid.words) {
      const cells = getWordCells(word);
      let solved = true;
      let nonSpaceCount = 0;
      for (const cell of cells) {
        if (cell.isSpace) continue;
        nonSpaceCount++;
        const key = `${cell.row}_${cell.col}`;
        const inputChar = userLetters[key] || '';
        if (!tamazightCharMatches(inputChar, cell.char)) {
          solved = false;
          break;
        }
      }
      if (solved && nonSpaceCount > 0) set.add(word.id);
    }
    return set;
  }, [selectedGrid, userLetters]);

  const isWordSolved = useCallback((word: CrosswordWord) => {
    return solvedWordIds.has(word.id);
  }, [solvedWordIds]);

  // Solved words count
  const solvedWordsCount = solvedWordIds.size;

  // Canonical proper letters for completed cells or individually revealed cells: `${r}_${c}` -> exact official char from word.kab
  const canonicalSolvedLetters = useMemo(() => {
    const map = new Map<string, string>();
    if (!selectedGrid) return map;
    const revealedSet = new Set(revealedCellKeys);
    for (const word of selectedGrid.words) {
      const isSolved = solvedWordIds.has(word.id);
      const cells = getWordCells(word);
      for (const cell of cells) {
        if (!cell.isSpace) {
          const key = `${cell.row}_${cell.col}`;
          if (isSolved || revealedSet.has(key)) {
            map.set(key, cell.char);
          }
        }
      }
    }
    return map;
  }, [selectedGrid, solvedWordIds, revealedCellKeys]);

  // Set of cell coordinates `${r}_${c}` that belong to an already solved word OR were revealed with the clue tool (locked cells)
  const lockedCellKeys = useMemo(() => {
    const set = new Set<string>();
    if (!selectedGrid) return set;
    for (const word of selectedGrid.words) {
      if (solvedWordIds.has(word.id)) {
        const cells = getWordCells(word);
        for (const cell of cells) {
          set.add(`${cell.row}_${cell.col}`);
        }
      }
    }
    for (const key of revealedCellKeys) {
      set.add(key);
    }
    return set;
  }, [selectedGrid, solvedWordIds, revealedCellKeys]);

  // When a new word is solved, automatically update userLetters on the grid with official Tamazight letters
  const prevSolvedCountRef = useRef(0);
  useEffect(() => {
    if (!selectedGrid || solvedWordIds.size === 0) return;
    if (solvedWordIds.size === prevSolvedCountRef.current) return;
    prevSolvedCountRef.current = solvedWordIds.size;

    setUserLetters((prev) => {
      let needsUpdate = false;
      const updated = { ...prev };

      for (const word of selectedGrid.words) {
        if (solvedWordIds.has(word.id)) {
          const cells = getWordCells(word);
          for (const cell of cells) {
            if (!cell.isSpace) {
              const key = `${cell.row}_${cell.col}`;
              if (updated[key] !== cell.char) {
                updated[key] = cell.char;
                needsUpdate = true;
              }
            }
          }
        }
      }

      return needsUpdate ? updated : prev;
    });
  }, [selectedGrid, solvedWordIds]);

  // Calculate progressive reveal count in evolving mode
  const revealedCount = useMemo(() => {
    if (!selectedGrid) return 1;
    if (!selectedGrid.isEvolving) return selectedGrid.words.length;
    // Find highest index among currently solved words
    let highestSolvedIdx = -1;
    selectedGrid.words.forEach((w, idx) => {
      if (solvedWordIds.has(w.id)) {
        if (idx > highestSolvedIdx) highestSolvedIdx = idx;
      }
    });
    // If word at idx is solved, reveal up to idx + 2 (next attached word)
    const autoRevealed = highestSolvedIdx >= 0 ? highestSolvedIdx + 2 : 1;
    return Math.min(selectedGrid.words.length, Math.max(manualRevealedCount, autoRevealed));
  }, [selectedGrid, solvedWordIds, manualRevealedCount]);

  // Persist revealed count to localStorage
  useEffect(() => {
    if (!selectedGrid || !selectedGrid.isEvolving) return;
    try {
      localStorage.setItem(
        `tamazight_crossword_revealed_${selectedGrid.id}`,
        String(revealedCount)
      );
    } catch {}
  }, [selectedGrid, revealedCount]);

  // Subset of words that are currently unlocked and visible on the board
  const visibleWords = useMemo(() => {
    if (!selectedGrid) return [];
    if (!selectedGrid.isEvolving) return selectedGrid.words;
    return selectedGrid.words.slice(0, revealedCount);
  }, [selectedGrid, revealedCount]);

  const visibleWordIds = useMemo(() => {
    return new Set(visibleWords.map((w) => w.id));
  }, [visibleWords]);

  // Matrix containing only currently revealed words
  const { matrix } = useMemo(() => {
    if (!selectedGrid) return { matrix: [], cellMap: {} };
    return buildGridMatrix(selectedGrid, visibleWordIds);
  }, [selectedGrid, visibleWordIds]);

  // Compute number of rows currently needed to display all revealed words
  const visibleRowsCount = useMemo(() => {
    if (!selectedGrid) return 0;
    if (!selectedGrid.isEvolving) return selectedGrid.rows;
    let maxR = 0;
    for (const w of visibleWords) {
      const cells = getWordCells(w);
      for (const c of cells) {
        if (c.row > maxR) maxR = c.row;
      }
    }
    return Math.min(selectedGrid.rows, maxR + 1);
  }, [selectedGrid, visibleWords]);

  // Compute number of columns currently needed to display all revealed words
  const visibleColsCount = useMemo(() => {
    if (!selectedGrid) return 0;
    if (!selectedGrid.isEvolving) return selectedGrid.cols;
    let maxC = 0;
    for (const w of visibleWords) {
      const cells = getWordCells(w);
      for (const c of cells) {
        if (c.col > maxC) maxC = c.col;
      }
    }
    return Math.max(8, Math.min(selectedGrid.cols, maxC + 1));
  }, [selectedGrid, visibleWords]);

  // When a word is solved and a new word attaches, play sound and auto-focus the new word
  const prevRevealedCountRef = useRef(revealedCount);
  useEffect(() => {
    if (revealedCount > prevRevealedCountRef.current && selectedGrid?.isEvolving) {
      const newWordIdx = revealedCount - 1;
      const newWord = selectedGrid.words[newWordIdx];
      if (newWord) {
        playSound('stele');
        const attachDesc = newWord.attachType === 'intersect' ? 'croisé' : 'rattaché en dessous';
        showToast(`✨ Mot n°${newWordIdx + 1} ${attachDesc} !`);
        setActiveWordId(newWord.id);
        setActiveCell({ row: newWord.row, col: newWord.col });
      }
    }
    prevRevealedCountRef.current = revealedCount;
  }, [revealedCount, selectedGrid]);

  // Container-isolated auto-scroll: strictly moves gridContainer scroll position WITHOUT bubbling or scrolling the outer screen
  const scrollActiveCellIntoGrid = useCallback(() => {
    const container = gridContainerRef.current;
    const cell = activeCellRef.current;
    if (!container || !cell) return;

    try {
      const cellRect = cell.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      const padding = 16;
      const isAbove = cellRect.top < containerRect.top + padding;
      const isBelow = cellRect.bottom > containerRect.bottom - padding;
      const isLeft = cellRect.left < containerRect.left + padding;
      const isRight = cellRect.right > containerRect.right - padding;

      // If the cell is fully visible, keep the user's chosen displacement position completely intact!
      if (!isAbove && !isBelow && !isLeft && !isRight) {
        return;
      }

      let newScrollTop = container.scrollTop;
      let newScrollLeft = container.scrollLeft;

      if (isAbove) {
        newScrollTop = Math.max(0, container.scrollTop + (cellRect.top - containerRect.top) - padding);
      } else if (isBelow) {
        newScrollTop = container.scrollTop + (cellRect.bottom - containerRect.bottom) + padding;
      }

      if (isLeft) {
        newScrollLeft = Math.max(0, container.scrollLeft + (cellRect.left - containerRect.left) - padding);
      } else if (isRight) {
        newScrollLeft = container.scrollLeft + (cellRect.right - containerRect.right) + padding;
      }

      if (Math.abs(newScrollTop - container.scrollTop) > 2 || Math.abs(newScrollLeft - container.scrollLeft) > 2) {
        container.scrollTo({
          top: newScrollTop,
          left: newScrollLeft,
          behavior: 'smooth',
        });
        savedDisplacementRef.current = { left: newScrollLeft, top: newScrollTop };
        if (selectedGridRef.current) {
          try {
            localStorage.setItem(
              `tamazight_crossword_scroll_${selectedGridRef.current.id}`,
              JSON.stringify({ left: newScrollLeft, top: newScrollTop })
            );
          } catch {}
        }
      }
    } catch {}
  }, []);

  // Auto-scroll active cell strictly within grid container
  useEffect(() => {
    if (activeCellRef.current) {
      scrollActiveCellIntoGrid();
    }
  }, [activeCell, activeWordId, scrollActiveCellIntoGrid]);

  // CRITICAL: When the player types/fills the grid (userLetters updates), strictly maintain the chosen displacement position!
  useLayoutEffect(() => {
    const container = gridContainerRef.current;
    if (!container || !hasUserDisplacedRef.current) return;

    if (
      Math.abs(container.scrollLeft - savedDisplacementRef.current.left) > 1 ||
      Math.abs(container.scrollTop - savedDisplacementRef.current.top) > 1
    ) {
      container.scrollLeft = savedDisplacementRef.current.left;
      container.scrollTop = savedDisplacementRef.current.top;
    }
  }, [userLetters]);

  // Check if entire grid is complete
  const isGridComplete = useMemo(() => {
    if (!selectedGrid) return false;
    return selectedGrid.words.length > 0 && solvedWordsCount === selectedGrid.words.length;
  }, [selectedGrid, solvedWordsCount]);

  // Find active word
  const activeWord = useMemo(() => {
    if (!selectedGrid || !activeWordId) return null;
    return selectedGrid.words.find((w) => w.id === activeWordId) || null;
  }, [selectedGrid, activeWordId]);

  // Select a grid to play & load saved letters with zero-data-loss synchronization
  const handleSelectGrid = (grid: CrosswordGrid) => {
    playSound('click');

    // 1. Flush the current active grid's state synchronously to prevent state race condition
    if (selectedGrid) {
      try {
        localStorage.setItem(`tamazight_crossword_letters_${selectedGrid.id}`, JSON.stringify(userLetters));
        localStorage.setItem(`tamazight_crossword_revealed_${selectedGrid.id}`, manualRevealedCount.toString());
        localStorage.setItem(`tamazight_crossword_revealed_cells_${selectedGrid.id}`, JSON.stringify(revealedCellKeys));
        if (activeWordId) {
          localStorage.setItem(`tamazight_crossword_active_word_${selectedGrid.id}`, activeWordId);
        }
        if (activeCell) {
          localStorage.setItem(`tamazight_crossword_active_cell_${selectedGrid.id}`, JSON.stringify(activeCell));
        }
      } catch {}
    }

    // 2. Load saved letters and revealed progression for the newly selected grid
    let savedLetters: Record<string, string> = {};
    let savedRevealed = 1;
    let savedRevealedCells: string[] = [];
    try {
      const raw = localStorage.getItem(`tamazight_crossword_letters_${grid.id}`);
      if (raw) savedLetters = JSON.parse(raw);
      const rawRev = localStorage.getItem(`tamazight_crossword_revealed_${grid.id}`);
      if (rawRev) savedRevealed = parseInt(rawRev, 10) || 1;
      const rawRevCells = localStorage.getItem(`tamazight_crossword_revealed_cells_${grid.id}`);
      if (rawRevCells) savedRevealedCells = JSON.parse(rawRevCells);
    } catch {}

    // 3. Auto-heal: If the grid was previously recorded as solved in solvedGridIds, ensure all letters are filled!
    const isTargetGridSolved = solvedGridIds.includes(grid.id);
    if (isTargetGridSolved) {
      let missingAny = false;
      grid.words.forEach((w) => {
        const cells = getWordCells(w);
        cells.forEach((c) => {
          if (!c.isSpace) {
            const key = `${c.row}_${c.col}`;
            if (savedLetters[key] !== c.char) {
              savedLetters[key] = c.char;
              missingAny = true;
            }
          }
        });
      });
      if (missingAny) {
        try {
          localStorage.setItem(`tamazight_crossword_letters_${grid.id}`, JSON.stringify(savedLetters));
        } catch {}
      }
    }

    userLettersRef.current = savedLetters;
    setSelectedGrid(grid);
    setUserLetters(savedLetters);
    setManualRevealedCount(savedRevealed);
    setRevealedCellKeys(savedRevealedCells);
    setShowVictoryModal(false);
    setShowHintsModal(false);
    setShowAllCluesModal(false);
    setGridCompletedJustNow(false);

    // Try to restore saved active word and cell, or default to first word
    const restored = restoreSavedWordAndCell(grid);
    if (!restored && grid.words.length > 0) {
      const firstWord = grid.words[0];
      setActiveWordId(firstWord.id);
      setActiveCell({ row: firstWord.row, col: firstWord.col });
    }

    // Restore saved displacement position
    restoreGridPosition(grid.id);

    // Trigger overlay if selected grid is empty
    const hasLetters = Object.values(savedLetters).some(
      (val) => typeof val === 'string' && val.trim().length > 0
    );
    if (!hasLetters && savedRevealedCells.length === 0) {
      shownGridHintIdsRef.current.add(grid.id);
      setTimeout(() => {
        triggerScrollHint(true);
      }, 250);
    }
  };

  // Auto-select word when grid is initialized if none valid
  useEffect(() => {
    if (!selectedGrid || selectedGrid.words.length === 0) return;
    const isValidActiveWord = activeWordId && selectedGrid.words.some((w) => w.id === activeWordId);
    if (!isValidActiveWord) {
      const restored = restoreSavedWordAndCell(selectedGrid);
      if (!restored) {
        const firstWord = selectedGrid.words[0];
        setActiveWordId(firstWord.id);
        setActiveCell({ row: firstWord.row, col: firstWord.col });
      }
    }
    restoreGridPosition(selectedGrid.id);
  }, [selectedGrid?.id, restoreSavedWordAndCell, restoreGridPosition]);

  const activeWordIdRef = useRef(activeWordId);
  activeWordIdRef.current = activeWordId;
  const activeCellRefVal = useRef(activeCell);
  activeCellRefVal.current = activeCell;

  // Auto-save active word & cell to localStorage (debounced by 300ms to avoid I/O lag)
  useEffect(() => {
    if (!selectedGrid) return;
    const gridId = selectedGrid.id;
    const wordIdToSave = activeWordId;
    const cellToSave = activeCell;

    const timer = setTimeout(() => {
      if (wordIdToSave) {
        try {
          localStorage.setItem(`tamazight_crossword_active_word_${gridId}`, wordIdToSave);
        } catch {}
      }
      if (cellToSave) {
        try {
          localStorage.setItem(`tamazight_crossword_active_cell_${gridId}`, JSON.stringify(cellToSave));
        } catch {}
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [selectedGrid?.id, activeWordId, activeCell]);

  // Auto-save letters to localStorage when updated (capturing current gridId & letters in closure)
  useEffect(() => {
    if (!selectedGrid) return;
    const gridId = selectedGrid.id;
    const lettersToSave = userLetters;

    const timer = setTimeout(() => {
      try {
        localStorage.setItem(
          `tamazight_crossword_letters_${gridId}`,
          JSON.stringify(lettersToSave)
        );
      } catch {}
    }, 200);

    return () => {
      clearTimeout(timer);
      try {
        localStorage.setItem(
          `tamazight_crossword_letters_${gridId}`,
          JSON.stringify(lettersToSave)
        );
      } catch {}
    };
  }, [selectedGrid?.id, userLetters]);

  // Handle victory when grid is solved
  useEffect(() => {
    if (selectedGrid && isGridComplete && !gridCompletedJustNow) {
      setGridCompletedJustNow(true);
      playSound('victory');
      setShowVictoryModal(true);

      // Instantly ensure all letters of the solved grid are saved
      try {
        localStorage.setItem(`tamazight_crossword_letters_${selectedGrid.id}`, JSON.stringify(userLetters));
      } catch {}

      // Save solved status
      if (!solvedGridIds.includes(selectedGrid.id)) {
        const newSolved = [...solvedGridIds, selectedGrid.id];
        setSolvedGridIds(newSolved);
        try {
          localStorage.setItem(STORAGE_SOLVED_KEY, JSON.stringify(newSolved));
        } catch {}

        // Award XP
        if (setScore) {
          setScore((prev) => prev + selectedGrid.xpReward);
        }
        if (onAddScore) {
          onAddScore(selectedGrid.xpReward);
        }
      }
    }
  }, [isGridComplete, selectedGrid, gridCompletedJustNow, solvedGridIds, userLetters, setScore, onAddScore]);

  // Auto-advance to next unsolved word when current active word gets completed
  useEffect(() => {
    if (!selectedGrid || !activeWordId || isGridComplete) return;
    if (solvedWordIds.has(activeWordId)) {
      const words = visibleWords.length > 0 ? visibleWords : selectedGrid.words;
      const currentIdx = words.findIndex((w) => w.id === activeWordId);
      // Look for next unsolved word starting after currentIdx, then wrap around
      const nextUnsolved =
        words.slice(currentIdx + 1).find((w) => !solvedWordIds.has(w.id)) ||
        words.find((w) => !solvedWordIds.has(w.id));

      if (nextUnsolved) {
        const timer = setTimeout(() => {
          setActiveWordId(nextUnsolved.id);
          setActiveCell({ row: nextUnsolved.row, col: nextUnsolved.col });
          playSound('correct');
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [solvedWordIds, activeWordId, visibleWords, isGridComplete, selectedGrid]);

  // Handle cell click
  const handleCellClick = useCallback(
    (r: number, c: number) => {
      if (hasDraggedRef.current) {
        hasDraggedRef.current = false;
        return;
      }
      const cell = matrix[r]?.[c];
      if (!cell || cell.isSpace) return;
      playSound('click');

      // Find all visible words whose FIRST non-space letter starts exactly at this cell (r, c)
      const wordsStartingHere = visibleWords.filter((w) => {
        const cells = getWordCells(w).filter((cellItem) => !cellItem.isSpace);
        return cells.length > 0 && cells[0].row === r && cells[0].col === c;
      });

      // If clicking already selected cell, toggle direction between Across / Down if both exist
      if (activeCell && activeCell.row === r && activeCell.col === c) {
        if (cell.acrossWordId && cell.downWordId) {
          setActiveWordId((cur) => (cur === cell.acrossWordId ? cell.downWordId! : cell.acrossWordId!));
          return;
        }
      }

      setActiveCell({ row: r, col: c });

      // 1. Requirement: When clicking on the first letter of a word, ALWAYS display that word's definition!
      if (wordsStartingHere.length > 0) {
        if (wordsStartingHere.length === 1) {
          setActiveWordId(wordsStartingHere[0].id);
          return;
        }
        // If two words start at this exact same cell (one Across, one Down)
        const acrossStart = wordsStartingHere.find((w) => w.direction === 'across' || w.isPhrase);
        const downStart = wordsStartingHere.find((w) => w.direction === 'down' && !w.isPhrase);
        if (activeWordId === acrossStart?.id && downStart) {
          setActiveWordId(downStart.id);
        } else if (activeWordId === downStart?.id && acrossStart) {
          setActiveWordId(acrossStart.id);
        } else {
          // Prefer unsolved word if one is unsolved
          const unsolved = wordsStartingHere.find((w) => !solvedWordIds.has(w.id));
          setActiveWordId(unsolved ? unsolved.id : wordsStartingHere[0].id);
        }
        return;
      }

      // 2. If no word starts here, but this cell is already part of current active word, keep current active word
      if (activeWord) {
        const activeCells = getWordCells(activeWord);
        if (activeCells.some((cellItem) => cellItem.row === r && cellItem.col === c)) {
          return;
        }
      }

      // 3. Otherwise, select passing word through this cell (preferring unsolved word)
      const acrossWord = cell.acrossWordId ? visibleWords.find((w) => w.id === cell.acrossWordId) : null;
      const downWord = cell.downWordId ? visibleWords.find((w) => w.id === cell.downWordId) : null;

      if (acrossWord && !solvedWordIds.has(acrossWord.id)) {
        setActiveWordId(acrossWord.id);
      } else if (downWord && !solvedWordIds.has(downWord.id)) {
        setActiveWordId(downWord.id);
      } else if (cell.acrossWordId) {
        setActiveWordId(cell.acrossWordId);
      } else if (cell.downWordId) {
        setActiveWordId(cell.downWordId);
      }
    },
    [matrix, visibleWords, activeCell, activeWordId, activeWord, solvedWordIds]
  );

  // Select a word from definitions list
  const handleSelectWord = (word: CrosswordWord) => {
    playSound('click');
    setActiveWordId(word.id);
    const firstCell = getWordCells(word).find((c) => !c.isSpace);
    if (firstCell) {
      setActiveCell({ row: firstCell.row, col: firstCell.col });
    } else {
      setActiveCell({ row: word.row, col: word.col });
    }
    setShowAllCluesModal(false);
  };

  // Navigate next/previous word
  const handleNavigateWord = (direction: 'prev' | 'next') => {
    if (!selectedGrid || !activeWordId) return;
    const words = visibleWords.length > 0 ? visibleWords : selectedGrid.words;
    const currentIdx = words.findIndex((w) => w.id === activeWordId);
    if (currentIdx === -1) {
      const first = words[0];
      if (first) {
        playSound('click');
        setActiveWordId(first.id);
        const firstCell = getWordCells(first).find((c) => !c.isSpace);
        setActiveCell(firstCell ? { row: firstCell.row, col: firstCell.col } : { row: first.row, col: first.col });
      }
      return;
    }

    let targetIdx = direction === 'next' ? currentIdx + 1 : currentIdx - 1;
    if (targetIdx >= words.length) targetIdx = 0;
    if (targetIdx < 0) targetIdx = words.length - 1;

    const nextWord = words[targetIdx];
    playSound('click');
    setActiveWordId(nextWord.id);
    const firstCell = getWordCells(nextWord).find((c) => !c.isSpace);
    setActiveCell(firstCell ? { row: firstCell.row, col: firstCell.col } : { row: nextWord.row, col: nextWord.col });
  };

  // Input character in current cell and advance
  const handleInputChar = useCallback(
    (rawChar: string) => {
      if (!selectedGrid || !activeCell || !activeWord) return;

      // 1. Requirement: If active word is already completed, letters CANNOT be changed!
      if (solvedWordIds.has(activeWord.id)) {
        showToast('🔒 Ce mot est déjà complété !');
        return;
      }

      const wordCells = getWordCells(activeWord);
      const currentIndex = wordCells.findIndex(
        (c) => c.row === activeCell.row && c.col === activeCell.col
      );
      if (currentIndex === -1) return;

      let targetIndex = currentIndex;
      const currentTargetCell = wordCells[targetIndex];
      const currentTargetKey = `${currentTargetCell.row}_${currentTargetCell.col}`;

      // 2. Requirement: If current cell is locked or space, skip to next unlocked cell!
      if (currentTargetCell.isSpace || lockedCellKeys.has(currentTargetKey)) {
        let nextUnlocked = -1;
        for (let i = currentIndex + 1; i < wordCells.length; i++) {
          const c = wordCells[i];
          const k = `${c.row}_${c.col}`;
          if (!c.isSpace && !lockedCellKeys.has(k)) {
            nextUnlocked = i;
            break;
          }
        }
        if (nextUnlocked === -1) {
          showToast('🔒 Lettres verrouillées');
          return;
        }
        targetIndex = nextUnlocked;
      }

      const targetCell = wordCells[targetIndex];
      const targetKey = `${targetCell.row}_${targetCell.col}`;
      const char = rawChar.toUpperCase();

      setUserLetters((prev) => ({
        ...prev,
        [targetKey]: char,
      }));

      playSound('step');

      // Move cursor to next UNLOCKED, NON-SPACE cell in current word
      for (let i = targetIndex + 1; i < wordCells.length; i++) {
        const nextCell = wordCells[i];
        const nextKey = `${nextCell.row}_${nextCell.col}`;
        if (!nextCell.isSpace && !lockedCellKeys.has(nextKey)) {
          setActiveCell({ row: nextCell.row, col: nextCell.col });
          break;
        }
      }
    },
    [selectedGrid, activeCell, activeWord, solvedWordIds, lockedCellKeys]
  );

  // Handle backspace
  const handleBackspace = useCallback(() => {
    if (!selectedGrid || !activeCell || !activeWord) return;

    // 1. Requirement: If active word is completed, do not delete any letters
    if (solvedWordIds.has(activeWord.id)) {
      showToast('🔒 Ce mot est déjà complété !');
      return;
    }

    const wordCells = getWordCells(activeWord);
    const currentIndex = wordCells.findIndex(
      (c) => c.row === activeCell.row && c.col === activeCell.col
    );
    if (currentIndex === -1) return;

    const currentCell = wordCells[currentIndex];
    const cellKey = `${currentCell.row}_${currentCell.col}`;

    // 2. If current cell has a letter and is NOT locked, clear it
    if (userLetters[cellKey] && !lockedCellKeys.has(cellKey) && !currentCell.isSpace) {
      setUserLetters((prev) => {
        const next = { ...prev };
        delete next[cellKey];
        return next;
      });
      playSound('click');
      return;
    }

    // 3. Otherwise move backwards to the previous UNLOCKED, NON-SPACE cell in this word and clear it
    if (currentIndex > 0) {
      let prevUnlockedIdx = -1;
      for (let i = currentIndex - 1; i >= 0; i--) {
        const c = wordCells[i];
        const k = `${c.row}_${c.col}`;
        if (!c.isSpace && !lockedCellKeys.has(k)) {
          prevUnlockedIdx = i;
          break;
        }
      }

      if (prevUnlockedIdx >= 0) {
        const prevCell = wordCells[prevUnlockedIdx];
        const prevKey = `${prevCell.row}_${prevCell.col}`;

        setActiveCell({ row: prevCell.row, col: prevCell.col });
        setUserLetters((prev) => {
          const next = { ...prev };
          delete next[prevKey];
          return next;
        });
        playSound('click');
      }
    }
  }, [selectedGrid, activeCell, activeWord, userLetters, solvedWordIds, lockedCellKeys]);

  // Handle space navigation for phrases
  const handleSpace = useCallback(() => {
    if (!activeWord || !activeCell) return;
    const cells = getWordCells(activeWord);
    const curIdx = cells.findIndex((c) => c.row === activeCell.row && c.col === activeCell.col);
    if (curIdx !== -1) {
      for (let i = curIdx + 1; i < cells.length; i++) {
        const cell = cells[i];
        const key = `${cell.row}_${cell.col}`;
        if (!cell.isSpace && !lockedCellKeys.has(key)) {
          setActiveCell({ row: cell.row, col: cell.col });
          playSound('step');
          break;
        }
      }
    }
  }, [activeWord, activeCell, lockedCellKeys]);

  // Physical Keyboard listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedGrid) return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (e.key === 'Backspace') {
        e.preventDefault();
        handleBackspace();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (activeCell && activeCell.col < selectedGrid.cols - 1) {
          const nextC = activeCell.col + 1;
          if (matrix[activeCell.row]?.[nextC]) {
            setActiveCell({ row: activeCell.row, col: nextC });
          }
        }
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (activeCell && activeCell.col > 0) {
          const prevC = activeCell.col - 1;
          if (matrix[activeCell.row]?.[prevC]) {
            setActiveCell({ row: activeCell.row, col: prevC });
          }
        }
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (activeCell && activeCell.row < selectedGrid.rows - 1) {
          const nextR = activeCell.row + 1;
          if (matrix[nextR]?.[activeCell.col]) {
            setActiveCell({ row: nextR, col: activeCell.col });
          }
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (activeCell && activeCell.row > 0) {
          const prevR = activeCell.row - 1;
          if (matrix[prevR]?.[activeCell.col]) {
            setActiveCell({ row: prevR, col: activeCell.col });
          }
        }
      } else if (e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        if (activeWord && activeCell) {
          const cells = getWordCells(activeWord);
          const curIdx = cells.findIndex((c) => c.row === activeCell.row && c.col === activeCell.col);
          if (curIdx !== -1) {
            for (let i = curIdx + 1; i < cells.length; i++) {
              const cell = cells[i];
              const key = `${cell.row}_${cell.col}`;
              if (!cell.isSpace && !lockedCellKeys.has(key)) {
                setActiveCell({ row: cell.row, col: cell.col });
                playSound('step');
                break;
              }
            }
          }
        }
      } else if (/^[a-zA-ZÀ-ÿɛɣḥṣṭḍčǧžṛẓƐƔḤṢṬḌČǦŽṚẒ]$/i.test(e.key)) {
        e.preventDefault();
        handleInputChar(e.key);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedGrid, activeCell, activeWord, lockedCellKeys, matrix, handleBackspace, handleInputChar]);

  // Reset current grid
  const handleResetGrid = () => {
    playSound('click');
    setUserLetters({});
    setManualRevealedCount(1);
    setRevealedCountPerWord({});
    setRevealedCellKeys([]);
    setShowVictoryModal(false);
    setGridCompletedJustNow(false);
    if (selectedGrid) {
      setSolvedGridIds((prev) => {
        const next = prev.filter((id) => id !== selectedGrid.id);
        try {
          localStorage.setItem(STORAGE_SOLVED_KEY, JSON.stringify(next));
        } catch {}
        return next;
      });
      shownGridHintIdsRef.current.delete(selectedGrid.id);
      try {
        localStorage.removeItem(`tamazight_crossword_letters_${selectedGrid.id}`);
        localStorage.removeItem(`tamazight_crossword_revealed_${selectedGrid.id}`);
        localStorage.removeItem(`tamazight_crossword_revealed_cells_${selectedGrid.id}`);
        localStorage.removeItem(`tamazight_crossword_scroll_${selectedGrid.id}`);
        localStorage.removeItem(`tamazight_crossword_active_word_${selectedGrid.id}`);
        localStorage.removeItem(`tamazight_crossword_active_cell_${selectedGrid.id}`);
      } catch {}
      savedDisplacementRef.current = { left: 0, top: 0 };
      hasUserDisplacedRef.current = false;
      if (gridContainerRef.current) {
        gridContainerRef.current.scrollLeft = 0;
        gridContainerRef.current.scrollTop = 0;
      }
      if (selectedGrid.words.length > 0) {
        const firstWord = selectedGrid.words[0];
        setActiveWordId(firstWord.id);
        setActiveCell({ row: firstWord.row, col: firstWord.col });
      }
    }
    // Show gesture coaching overlay upon resetting grid
    setTimeout(() => {
      triggerScrollHint(true);
    }, 200);
  };

  // Reveal one letter for the active word (up to 50% of the word's letter composition)
  const handleRevealSingleLetter = useCallback(() => {
    if (!activeWord) {
      showToast('Touchez une case pour sélectionner un mot');
      return;
    }
    const cells = getWordCells(activeWord).filter((c) => !c.isSpace);
    const totalLetters = cells.length;
    // Up to 50% of letters (e.g. 4 letters -> max 2; 5 letters -> max 2; 6 letters -> max 3; 3 letters -> max 1)
    const maxAllowed = Math.max(1, Math.floor(totalLetters * 0.5));
    const currentRevealed = cells.filter((c) => revealedCellKeys.includes(`${c.row}_${c.col}`)).length;

    if (currentRevealed >= maxAllowed) {
      showToast(`⚠️ Limite de 50% atteinte (${currentRevealed}/${totalLetters} lettres révélées pour ce mot)`);
      playSound('wrong');
      return;
    }

    // Find the first letter that is not yet revealed and not yet correctly filled
    const targetCell = cells.find((c) => {
      const key = `${c.row}_${c.col}`;
      if (revealedCellKeys.includes(key) || lockedCellKeys.has(key)) return false;
      const inputChar = userLetters[key] || '';
      return !tamazightCharMatches(inputChar, c.char);
    });

    if (!targetCell) {
      showToast('Ce mot est déjà entièrement trouvé !');
      return;
    }

    const cellKey = `${targetCell.row}_${targetCell.col}`;
    setUserLetters((prev) => {
      const next = { ...prev, [cellKey]: targetCell.char };
      if (selectedGrid) {
        try {
          localStorage.setItem(`tamazight_crossword_letters_${selectedGrid.id}`, JSON.stringify(next));
        } catch {}
      }
      return next;
    });

    setRevealedCellKeys((prev) => {
      if (prev.includes(cellKey)) return prev;
      const next = [...prev, cellKey];
      if (selectedGrid) {
        try {
          localStorage.setItem(`tamazight_crossword_revealed_cells_${selectedGrid.id}`, JSON.stringify(next));
        } catch {}
      }
      return next;
    });

    setRevealedCountPerWord((prev) => ({
      ...prev,
      [activeWord.id]: currentRevealed + 1,
    }));

    // Advance cursor to next unlocked cell in the active word
    const targetIdx = cells.findIndex((c) => c.row === targetCell.row && c.col === targetCell.col);
    let nextAvailableCell: { row: number; col: number } | null = null;
    if (targetIdx !== -1) {
      for (let i = targetIdx + 1; i < cells.length; i++) {
        const c = cells[i];
        const k = `${c.row}_${c.col}`;
        if (!lockedCellKeys.has(k) && k !== cellKey) {
          nextAvailableCell = { row: c.row, col: c.col };
          break;
        }
      }
      if (!nextAvailableCell) {
        for (let i = 0; i < targetIdx; i++) {
          const c = cells[i];
          const k = `${c.row}_${c.col}`;
          if (!lockedCellKeys.has(k) && k !== cellKey) {
            nextAvailableCell = { row: c.row, col: c.col };
            break;
          }
        }
      }
    }

    if (nextAvailableCell) {
      setActiveCell(nextAvailableCell);
    } else {
      setActiveCell({ row: targetCell.row, col: targetCell.col });
    }

    playSound('correct');
    showToast(`💡 Lettre révélée : « ${targetCell.char} » (${currentRevealed + 1}/${maxAllowed})`);
  }, [activeWord, userLetters, revealedCellKeys, lockedCellKeys, selectedGrid]);

  // Reveal entire active word for 100 XP
  const WORD_REVEAL_XP_COST = 100;
  const handleRevealWholeWord = useCallback(() => {
    if (!activeWord) {
      showToast('Touchez une case pour sélectionner un mot');
      return;
    }
    if (isWordSolved(activeWord)) {
      showToast('Ce mot est déjà entièrement résolu !');
      return;
    }

    if (score < WORD_REVEAL_XP_COST) {
      playSound('wrong');
      showToast(`⚠️ XP insuffisant (${score}/${WORD_REVEAL_XP_COST} XP nécessaires)`);
      return;
    }

    const confirmMsg = `Révéler le mot entier « ${activeWord.fr} » sur la grille pour ${WORD_REVEAL_XP_COST} XP ?\n(Votre solde : ${score} XP)`;
    if (!window.confirm(confirmMsg)) {
      return;
    }

    // Deduct 100 XP
    if (onAddScore) {
      onAddScore(-WORD_REVEAL_XP_COST);
    }
    if (setScore) {
      setScore((prev) => Math.max(0, prev - WORD_REVEAL_XP_COST));
    }

    const cells = getWordCells(activeWord);
    const updatedLetters = { ...userLetters };
    const updatedRevealedKeys = [...revealedCellKeys];

    cells.forEach((c) => {
      if (!c.isSpace) {
        const key = `${c.row}_${c.col}`;
        updatedLetters[key] = c.char.toUpperCase();
        if (!updatedRevealedKeys.includes(key)) {
          updatedRevealedKeys.push(key);
        }
      }
    });

    setUserLetters(updatedLetters);
    setRevealedCellKeys(updatedRevealedKeys);
    setRevealedCountPerWord((prev) => ({
      ...prev,
      [activeWord.id]: cells.filter((c) => !c.isSpace).length,
    }));

    if (selectedGrid) {
      try {
        localStorage.setItem(`tamazight_crossword_letters_${selectedGrid.id}`, JSON.stringify(updatedLetters));
        localStorage.setItem(`tamazight_crossword_revealed_cells_${selectedGrid.id}`, JSON.stringify(updatedRevealedKeys));
      } catch {}
    }

    playSound('correct');
    triggerHaptic('medium');
    showToast(`✨ Mot révélé : « ${activeWord.originalKab} » (-${WORD_REVEAL_XP_COST} XP)`);
    setShowHintsModal(false);

    // Auto-advance to next unsolved word in current grid
    if (selectedGrid) {
      const nextUnsolvedWord = selectedGrid.words.find((w) => {
        if (w.id === activeWord.id) return false;
        const wCells = getWordCells(w).filter((c) => !c.isSpace);
        return !wCells.every((c) => {
          const key = `${c.row}_${c.col}`;
          const inputChar = updatedLetters[key] || '';
          return tamazightCharMatches(inputChar, c.char);
        });
      });
      if (nextUnsolvedWord) {
        setActiveWordId(nextUnsolvedWord.id);
        setActiveCell({ row: nextUnsolvedWord.row, col: nextUnsolvedWord.col });
      }
    }
  }, [activeWord, isWordSolved, score, userLetters, revealedCellKeys, selectedGrid, onAddScore, setScore, showToast]);

  return (
    <div className="fixed inset-0 w-full h-[100dvh] bg-[#fbf7ee] berber-weave-pattern text-stone-900 flex flex-col z-[120] overflow-hidden select-none font-mono overscroll-none touch-manipulation">
      {/* Top Rug Runner Border */}
      <BerberRugBorder />

      {/* Toast popup with smooth non-intrusive animation and click-to-dismiss */}
      <AnimatePresence>
        {toastMsg && (
          <motion.div
            key="crossword-toast"
            initial={{ opacity: 0, y: -10, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            onClick={() => setToastMsg(null)}
            className="absolute top-12 left-1/2 -translate-x-1/2 z-[80] max-w-[92%] sm:max-w-md w-auto pointer-events-auto cursor-pointer"
          >
            <div className="bg-[#fffdf9]/95 backdrop-blur-md border-2 border-amber-500 text-amber-950 font-black text-xs px-4 py-2 rounded-xl shadow-xl flex items-center justify-center gap-2 text-center">
              <span>{toastMsg}</span>
              <span className="text-stone-400 hover:text-stone-700 text-xs ml-1">✕</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ========================================================================= */}
      {/* 1. GRID SELECTION SCREEN                                                  */}
      {/* ========================================================================= */}
      {!selectedGrid && (
        <div className="w-full h-full flex flex-col overflow-hidden">
          {/* Header */}
          <div className="pt-3 pb-2.5 px-3 sm:px-6 bg-[#fffdf9] border-b-2 border-[#b45309]/30 shadow-xs flex items-center justify-between gap-2 shrink-0 z-30">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  playSound('click');
                  onBack();
                }}
                className="px-3 py-1.5 bg-[#fff1f2] hover:bg-[#ffe4e6] text-red-900 font-black text-xs uppercase tracking-wider border-2 border-red-300 rounded-lg shadow-xs active:translate-y-0.5 transition-all flex items-center gap-1.5 shrink-0 cursor-pointer"
              >
                <svg className="w-3 h-3 fill-current shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                  <polygon points="17,4 7,12 17,20" />
                </svg>
                <span>Retour</span>
              </button>
              <div>
                <h1 className="text-xs sm:text-sm font-black text-amber-950 uppercase tracking-wide flex items-center gap-1.5 font-cinzel">
                  <span>🧩</span>
                  <span>Mots Croisés Kabyles</span>
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-1.5 bg-amber-100/90 border border-amber-400 px-3 py-1 rounded-full text-xs font-bold text-amber-950 shadow-2xs">
              <span>Niveaux : {unlockedLevels.length}</span>
            </div>
          </div>

          {/* Grids list */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 overscroll-contain">
            {/* Info Banner */}
            <div className="bg-[#fffdf9] border-2 border-[#b45309]/30 rounded-2xl p-4 shadow-sm flex items-center gap-3">
              <span className="text-3xl shrink-0">🧩</span>
              <div>
                <h2 className="text-xs sm:text-sm font-black text-amber-950 uppercase font-cinzel">
                  Grilles de Mots Croisés
                </h2>
                <p className="text-xs text-stone-700 mt-0.5 leading-relaxed">
                  Complétez la grille en retrouvant chaque mot kabyle grâce aux définitions en français et aux caractères tifinagh/latin.
                </p>
              </div>
            </div>

            {/* Grid Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-4xl mx-auto pb-6">
              {CROSSWORD_GRIDS.map((grid) => {
                const isUnlocked = unlockedLevels.includes(grid.requiredLevel);
                const isSolved = solvedGridIds.includes(grid.id);

                return (
                  <div
                    key={grid.id}
                    className={`p-4 rounded-2xl border-2 flex flex-col justify-between gap-3 transition-all relative ${
                      isSolved
                        ? 'bg-[#f0fdf4] border-emerald-400 shadow-sm'
                        : isUnlocked
                        ? 'bg-[#fffdf9] border-[#b45309]/30 hover:border-amber-500 shadow-sm hover:shadow-md'
                        : 'bg-stone-100/70 border-stone-200 opacity-60'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-11 h-11 rounded-xl bg-amber-50 border-2 border-amber-300/80 flex items-center justify-center text-2xl shrink-0 shadow-xs">
                          {grid.icon}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-[9px] font-black px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300 uppercase">
                              {grid.difficulty}
                            </span>
                            <span className="text-[10px] text-stone-600 font-mono font-bold">
                              {grid.words.length} mots • {grid.rows}x{grid.cols}
                            </span>
                          </div>
                          <h3 className="font-black text-xs sm:text-sm text-amber-950 truncate mt-1 font-cinzel">
                            {grid.title}
                          </h3>
                          <p className="text-[11px] text-stone-600 truncate">{grid.subtitle}</p>
                        </div>
                      </div>

                      <div className="shrink-0 text-right">
                        {isSolved && (
                          <span className="text-[10px] bg-emerald-100 text-emerald-800 border border-emerald-400 px-2.5 py-0.5 rounded-full font-bold shadow-2xs">
                            ✓ Résolu
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="pt-2 border-t border-stone-200">
                      {isUnlocked ? (
                        <button
                          type="button"
                          onClick={() => handleSelectGrid(grid)}
                          className={`w-full py-2.5 px-3 font-black text-xs uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-xs active:scale-98 ${
                            isSolved
                              ? 'bg-emerald-700 hover:bg-emerald-600 text-white'
                              : 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-stone-950 border border-amber-600'
                          }`}
                        >
                          <span>{isSolved ? '🔄 Rejouer la Grille' : '▶ Jouer la Grille'}</span>
                        </button>
                      ) : (
                        <div className="text-[11px] text-stone-500 italic flex items-center justify-center gap-1.5 py-1">
                          <span>🔒</span>
                          <span>Déblocage au niveau {grid.region}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 2. ACTIVE CROSSWORD GAMEPLAY SCREEN (OPTIMIZED FOR MOBILE VIEWPORT)       */}
      {/* ========================================================================= */}
      {selectedGrid && (
        <div className="w-full flex-1 min-h-0 flex flex-col overflow-hidden">
          {/* A. ULTRA-COMPACT GAMEPLAY HEADER (PERFECTLY ALIGNED & HARMONIOUS) */}
          <header className="bg-[#fffdf9] border-b-2 border-[#b45309]/30 px-2.5 sm:px-4 py-2 flex items-center justify-between gap-2 shrink-0 shadow-xs z-20">
            {/* Left: Back button & Title */}
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <button
                type="button"
                onClick={() => {
                  playSound('click');
                  if (challengeGrid || challengeGrids) {
                    onBack();
                  } else {
                    setSelectedGrid(null);
                  }
                }}
                className="px-3 py-1.5 bg-[#fff1f2] hover:bg-[#ffe4e6] text-red-900 font-black text-xs uppercase tracking-wider border-2 border-red-300 rounded-lg shadow-xs active:translate-y-0.5 transition-all flex items-center gap-1.5 shrink-0 cursor-pointer"
              >
                <svg className="w-3 h-3 fill-current shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                  <polygon points="17,4 7,12 17,20" />
                </svg>
                <span>{challengeGrid || challengeGrids ? 'Défis' : 'Grilles'}</span>
              </button>

              <div className="min-w-0 flex items-center gap-1.5">
                <span className="text-base shrink-0">{selectedGrid.icon}</span>
                <h2 className="text-xs sm:text-sm font-black text-amber-950 truncate max-w-[130px] sm:max-w-[260px] font-cinzel">
                  {selectedGrid.title}
                </h2>
              </div>
            </div>

            {/* Right: Solved Progress & Clear/Reset Button */}
            <div className="flex items-center gap-1.5 shrink-0">
              {/* Solved counter badge */}
              <button
                type="button"
                onClick={() => setShowAllCluesModal(true)}
                className="h-8 px-2.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-950 border border-emerald-300 rounded-lg text-xs font-bold flex items-center gap-1.5 cursor-pointer active:scale-95 shadow-2xs transition-all"
                title="Progression : mots trouvés dans cette grille"
              >
                <span className="text-xs">📋</span>
                <span className="font-mono">
                  <span className="text-emerald-700 font-black">{solvedWordsCount}</span>
                  <span className="text-emerald-400 font-normal"> / </span>
                  <span className="text-stone-600 font-bold">{selectedGrid.words.length}</span>
                </span>
              </button>

              {/* Clear / Reset Grid button with clear label & styling */}
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Voulez-vous effacer vos réponses sur cette grille pour recommencer ?')) {
                    handleResetGrid();
                  }
                }}
                className="h-8 px-2 sm:px-2.5 bg-stone-100 hover:bg-rose-50 text-stone-700 hover:text-rose-800 border border-stone-300 hover:border-rose-300 rounded-lg text-xs font-bold flex items-center gap-1 cursor-pointer active:scale-95 shadow-2xs transition-all"
                title="Effacer vos réponses et recommencer la grille"
              >
                <svg className="w-3.5 h-3.5 shrink-0 text-stone-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span className="hidden sm:inline">Effacer</span>
              </button>
            </div>
          </header>

          {/* MULTI-GRID TABS (when a challenge has multiple <= 50 word parts) */}
          {challengeGrids && challengeGrids.length > 1 && (
            <div className="flex items-center gap-2 overflow-x-auto py-1.5 px-3 bg-[#fffdf9]/90 border-b border-[#b45309]/20 shrink-0">
              <span className="text-[10px] text-amber-900 font-black uppercase tracking-wider shrink-0 font-cinzel">
                Grilles :
              </span>
              {challengeGrids.map((g, idx) => {
                const isSolved = solvedGridIds.includes(g.id);
                const isCurrent = selectedGrid.id === g.id;
                return (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => handleSelectGrid(g)}
                    className={`px-3 py-1 rounded-full text-[11px] font-mono font-bold shrink-0 transition-all border-2 cursor-pointer flex items-center gap-1.5 ${
                      isCurrent
                        ? 'bg-amber-500 text-stone-950 border-amber-600 font-black shadow-xs'
                        : isSolved
                        ? 'bg-emerald-50 text-emerald-900 border-emerald-400 hover:bg-emerald-100'
                        : 'bg-[#fffdf9] text-stone-700 border-stone-300 hover:border-amber-400 hover:text-stone-950'
                    }`}
                  >
                    <span>Grille {idx + 1} ({g.words.length} mots)</span>
                    {isSolved && <span className="text-emerald-700 font-black text-xs">✓</span>}
                  </button>
                );
              })}
            </div>
          )}

          {/* B. MAIN INTERACTIVE GRID VIEWPORT WRAPPER (CENTERED SCHEMATIC OVERLAY + SCROLLABLE GRID) */}
          <div className="flex-1 min-h-0 w-full relative flex flex-col items-center justify-center overflow-hidden">
            {/* Apple-style Schematic Gesture Coaching Overlay (Centered on the grid viewport, Zero layout shift) */}
            <AnimatePresence>
              {showScrollHint && !toastMsg && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.82, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.88, y: -6 }}
                  transition={{ type: 'spring', stiffness: 380, damping: 26 }}
                  onClick={() => setShowScrollHint(false)}
                  className="absolute inset-0 z-40 flex items-center justify-center pointer-events-none p-4"
                >
                  <div className="pointer-events-auto bg-stone-950/85 backdrop-blur-md text-white border border-amber-400/35 shadow-2xl rounded-2xl p-4 sm:p-5 flex flex-col items-center justify-center gap-2.5 max-w-[240px] sm:max-w-[260px] text-center cursor-pointer transition-transform active:scale-95">
                    {/* Schematic Gesture Visual (Hand + 4-directional pulsing arrows) */}
                    <div className="relative w-16 h-16 flex items-center justify-center select-none">
                      {/* 4 Directional Arrows */}
                      <motion.div
                        animate={{ y: [-2, -5, -2] }}
                        transition={{ repeat: Infinity, duration: 1.3, ease: 'easeInOut' }}
                        className="absolute top-0 left-1/2 -translate-x-1/2 text-amber-400"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.8}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                        </svg>
                      </motion.div>
                      <motion.div
                        animate={{ y: [2, 5, 2] }}
                        transition={{ repeat: Infinity, duration: 1.3, ease: 'easeInOut' }}
                        className="absolute bottom-0 left-1/2 -translate-x-1/2 text-amber-400"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.8}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </motion.div>
                      <motion.div
                        animate={{ x: [-2, -5, -2] }}
                        transition={{ repeat: Infinity, duration: 1.3, ease: 'easeInOut' }}
                        className="absolute left-0 top-1/2 -translate-y-1/2 text-amber-400"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.8}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                        </svg>
                      </motion.div>
                      <motion.div
                        animate={{ x: [2, 5, 2] }}
                        transition={{ repeat: Infinity, duration: 1.3, ease: 'easeInOut' }}
                        className="absolute right-0 top-1/2 -translate-y-1/2 text-amber-400"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.8}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                      </motion.div>

                      {/* Schematic Apple-style Touch Hand with Finger Ring */}
                      <motion.div
                        animate={{
                          x: [-3, 3, -3],
                          y: [-2, 2, -2],
                        }}
                        transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
                        className="relative z-10 flex items-center justify-center"
                      >
                        {/* Touch Pulse Ripple at the fingertip */}
                        <span className="absolute -top-1 -right-0.5 w-3.5 h-3.5 bg-amber-400/50 rounded-full animate-ping pointer-events-none" />
                        <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-amber-400 rounded-full shadow-xs pointer-events-none" />

                        {/* Hand Pointer SVG */}
                        <svg
                          className="w-8 h-8 text-amber-100 filter drop-shadow-[0_2px_4px_rgba(0,0,0,0.6)]"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={1.8}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M18 11V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0" />
                          <path d="M14 10V4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v2" />
                          <path d="M10 10.5V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v8" />
                          <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
                        </svg>
                      </motion.div>
                    </div>

                    {/* Instruction Label */}
                    <div className="flex flex-col gap-0.5">
                      <span className="text-xs sm:text-sm font-bold font-mono text-amber-200 tracking-wide">
                        Glissez pour naviguer
                      </span>
                      <span className="text-[10px] sm:text-[11px] text-stone-300 font-sans leading-tight">
                        Explorez la grille dans toutes les directions
                      </span>
                    </div>

                    {/* Dismiss Pill */}
                    <div className="mt-0.5 px-2.5 py-0.5 rounded-full bg-white/10 text-[9px] sm:text-[10px] text-stone-400 font-mono flex items-center gap-1 border border-white/10">
                      <span>Touchez pour continuer</span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Scrollable Grid Viewport */}
            <div
              ref={gridContainerRef}
              onScroll={handleContainerScroll}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              className="w-full h-full overflow-auto touch-pan-x touch-pan-y overscroll-contain select-none cursor-grab active:cursor-grabbing relative"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              <div className="min-w-full min-h-full w-max h-max flex items-center justify-center p-3 sm:p-5">
                {/* Crossword Grid Table (Stably Anchored Without Layout Jumps) */}
                <div
                  className={`grid gap-1 sm:gap-1.5 p-2.5 sm:p-4 bg-[#fffdf9] rounded-2xl border-2 border-[#b45309]/30 shadow-md shrink-0 ${
                    visibleColsCount <= 8
                      ? 'w-full max-w-[360px] sm:max-w-[440px]'
                      : 'w-max max-w-none'
                  }`}
                  style={{
                    gridTemplateColumns:
                      visibleColsCount <= 8
                        ? `repeat(${visibleColsCount}, minmax(0, 1fr))`
                        : `repeat(${visibleColsCount}, minmax(36px, 42px))`,
                  }}
                >
              {Array.from({ length: visibleRowsCount }).map((_, r) =>
                Array.from({ length: visibleColsCount }).map((_, c) => {
                  const cell = matrix[r]?.[c];
                  const key = `${r}_${c}`;

                  const isPartOfActiveWord = Boolean(
                    activeWordId && (cell?.acrossWordId === activeWordId || cell?.downWordId === activeWordId)
                  );
                  const isCurrentActiveCell = Boolean(activeCell && activeCell.row === r && activeCell.col === c);
                  const currentLetter = canonicalSolvedLetters.get(key) || userLetters[key] || '';
                  const isCellLocked = lockedCellKeys.has(key);

                  // Check if words through this cell are solved
                  const acrossSolved = !cell?.acrossWordId || solvedWordIds.has(cell.acrossWordId);
                  const downSolved = !cell?.downWordId || solvedWordIds.has(cell.downWordId);
                  const isCellFullySolved = Boolean(acrossSolved && downSolved && currentLetter !== '');

                  const expectedLetter = cell?.expectedChar || cell?.char || '';

                  // Highlight cell in red when entered letter is incorrect
                  const isIncorrect = Boolean(
                    currentLetter !== '' &&
                    !isCellLocked &&
                    !isCellFullySolved &&
                    !tamazightCharMatches(currentLetter, expectedLetter)
                  );

                  return (
                    <CrosswordGridCell
                      key={key}
                      r={r}
                      c={c}
                      cell={cell}
                      isCurrentActiveCell={isCurrentActiveCell}
                      isPartOfActiveWord={isPartOfActiveWord}
                      currentLetter={currentLetter}
                      isCellLocked={isCellLocked}
                      isCellFullySolved={isCellFullySolved}
                      isIncorrect={isIncorrect}
                      onClick={handleCellClick}
                      cellRef={isCurrentActiveCell ? activeCellRef : null}
                    />
                  );
                })
              )}
                </div>
              </div>
            </div>
          </div>

          {/* C. ACTIVE CLUE BAR (SIMPLIFIED: "Traduis : [mot]") */}
          <div className="bg-[#fffdf9] border-t border-b border-amber-900/10 px-3 py-2 shrink-0 flex items-center justify-center text-center shadow-xs min-h-[44px]">
            {activeWord ? (
              <div className="w-full flex items-center justify-center gap-2 text-center flex-wrap">
                <p className="text-base sm:text-lg md:text-xl font-black text-amber-950 leading-snug font-cinzel">
                  <span className="text-amber-800 font-bold uppercase tracking-wider text-sm sm:text-base mr-1.5">
                    Traduis :
                  </span>
                  <span className="uppercase font-black text-amber-950">
                    {activeWord.fr}
                  </span>
                </p>
                {isWordSolved(activeWord) && (
                  <span className="text-emerald-800 font-bold text-[10px] bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-300 inline-flex items-center gap-1 shadow-2xs">
                    <span>✓</span>
                    <span>Trouvé</span>
                  </span>
                )}
              </div>
            ) : (
              <div className="text-xs sm:text-sm text-stone-500 text-center w-full py-0.5 font-semibold">
                Touchez une case de la grille pour afficher le mot à traduire
              </div>
            )}
          </div>

          {/* D. ERGONOMIC TAMAZIGHT VIRTUAL KEYBOARD (AZERTY + DEDICATED AMAZIGH RIBBON) */}
          <div className="bg-[#fffdf9] border-t-2 border-amber-900/20 px-1.5 pt-2 pb-2.5 sm:px-3 sm:pt-2.5 sm:pb-3 flex flex-col gap-1.5 shrink-0 max-w-xl mx-auto w-full shadow-lg select-none">
            <CrosswordVirtualKeyboard
              onInputChar={handleInputChar}
              onBackspace={handleBackspace}
              onSpace={handleSpace}
              isPhrase={Boolean(activeWord?.isPhrase)}
            />

            {/* CONTROLS TOOLBAR (BOLD & NEVER-WRAPPING) */}
            <div className="flex items-center justify-between gap-1.5 sm:gap-2 w-full pt-1 pb-0 select-none">
              {/* Reveal Letter Button (Max 50% of word) - Bold, Crisp & Single-Line */}
              <button
                type="button"
                onClick={handleRevealSingleLetter}
                disabled={!activeWord || isWordSolved(activeWord)}
                className="flex-1 h-9 sm:h-10 bg-gradient-to-r from-amber-600 via-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white font-black text-xs rounded-xl border border-amber-800/80 active:scale-95 transition-all flex items-center justify-center gap-1.5 px-2 cursor-pointer shadow-xs disabled:opacity-40 disabled:pointer-events-none"
                title="Révéler une lettre du mot actif (max 50% des lettres)"
              >
                <span className="text-sm shrink-0">💡</span>
                <span className="whitespace-nowrap tracking-wide font-black">
                  <span className="hidden xs:inline">Révéler </span>Lettre
                </span>
                {activeWord && (
                  <span className="text-[11px] bg-black/35 text-amber-100 border border-white/20 px-1.5 py-0.5 rounded-md font-mono font-black shrink-0">
                    {getWordCells(activeWord).filter((c) => !c.isSpace && revealedCellKeys.includes(`${c.row}_${c.col}`)).length}/{Math.max(1, Math.floor(getWordCells(activeWord).filter((c) => !c.isSpace).length * 0.5))}
                  </span>
                )}
              </button>

              {/* Audio Button - Bold */}
              {activeWord && (
                <button
                  type="button"
                  onClick={() => playTTS(activeWord.originalKab)}
                  className="h-9 sm:h-10 px-3 bg-sky-50 hover:bg-sky-100 text-sky-950 font-black text-xs rounded-xl border-2 border-sky-300 active:scale-95 transition-all flex items-center justify-center gap-1.5 shrink-0 cursor-pointer shadow-2xs"
                  title="Écouter la prononciation sans voir le mot"
                >
                  <span className="text-sm shrink-0">🎧</span>
                  <span className="whitespace-nowrap font-black">Audio</span>
                </button>
              )}

              {/* Hints / Révéler Mot Entier Button (100 XP) - Bold */}
              {activeWord && (
                <button
                  type="button"
                  onClick={handleRevealWholeWord}
                  disabled={isWordSolved(activeWord)}
                  className={`h-9 sm:h-10 px-2.5 sm:px-3 text-xs font-black rounded-xl border active:scale-95 transition-all flex items-center justify-center gap-1.5 shrink-0 cursor-pointer shadow-xs disabled:opacity-40 disabled:pointer-events-none ${
                    score >= 100
                      ? 'bg-gradient-to-r from-purple-700 to-indigo-800 hover:from-purple-600 hover:to-indigo-700 text-white border-purple-500/70'
                      : 'bg-stone-200 text-stone-600 border-stone-300'
                  }`}
                  title="Révéler et écrire le mot entier sur la grille pour 100 XP"
                >
                  <span className="text-sm shrink-0">👁️</span>
                  <span className="whitespace-nowrap font-black">
                    Mot <span className="font-mono text-[10px] sm:text-[11px] opacity-90">(100 XP)</span>
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 3. MODAL: INDICE SOLUTION & PRONONCIATION (AVEC RÉVÉLATION MOT 100 XP)    */}
      {/* ========================================================================= */}
      {showHintsModal && activeWord && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center p-3 sm:p-4 animate-in fade-in">
          <div className="bg-[#fffdf9] border-2 border-[#b45309]/40 rounded-2xl p-4 sm:p-5 max-w-sm sm:max-w-md w-full space-y-3 shadow-2xl animate-in zoom-in-95">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-stone-200 pb-2.5">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xl sm:text-2xl shrink-0">{activeWord.icon || '💡'}</span>
                <h3 className="font-black text-xs sm:text-sm text-amber-950 uppercase truncate font-cinzel">
                  Indice : « {activeWord.fr} »
                </h3>
              </div>

              <button
                type="button"
                onClick={() => setShowHintsModal(false)}
                className="p-1.5 text-stone-500 hover:text-stone-900 bg-stone-100 rounded-lg text-xs cursor-pointer active:scale-95 shrink-0"
                title="Fermer"
              >
                ✕
              </button>
            </div>

            {/* Clue card: Solution + Written Kabyle + Audio Pronunciation */}
            <div className="p-3.5 bg-amber-50 border-2 border-amber-300/80 rounded-xl flex items-center justify-between gap-3 shadow-inner">
              <div className="space-y-1 min-w-0">
                <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-amber-800 block font-mono">
                  Mot en kabyle :
                </span>
                <p className="font-mono text-stone-900 text-base sm:text-lg font-black tracking-wide break-words">
                  « {activeWord.originalKab} »
                </p>
                {activeWord.phonetic && activeWord.phonetic.toLowerCase() !== activeWord.originalKab.toLowerCase() && (
                  <p className="text-amber-800 text-xs font-mono">
                    ({activeWord.phonetic})
                  </p>
                )}
              </div>
              <TTSButton
                text={activeWord.originalKab}
                className="p-3 bg-amber-500 hover:bg-amber-400 border border-amber-600 rounded-xl text-stone-950 text-base shrink-0 cursor-pointer active:scale-95 shadow-xs"
                title="Écouter la prononciation"
              />
            </div>

            {/* Modal Actions */}
            <div className="space-y-2 pt-1">
              <button
                type="button"
                onClick={handleRevealWholeWord}
                disabled={isWordSolved(activeWord)}
                className={`w-full py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-all cursor-pointer shadow-xs active:scale-98 flex items-center justify-center gap-2 ${
                  score >= 100
                    ? 'bg-gradient-to-r from-purple-700 to-indigo-800 hover:from-purple-600 hover:to-indigo-700 text-white border border-purple-500/60'
                    : 'bg-stone-200 text-stone-500 border border-stone-300 cursor-not-allowed'
                }`}
                title="Inscrire la solution sur la grille (coûte 100 XP)"
              >
                <span>👁️</span>
                <span>Inscrire le mot sur la grille (-100 XP)</span>
              </button>

              <button
                type="button"
                onClick={() => setShowHintsModal(false)}
                className="w-full py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 font-bold text-xs uppercase tracking-wider rounded-xl transition-all cursor-pointer"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 4. MODAL: ALL DEFINITIONS / CLUES LIST                                    */}
      {/* ========================================================================= */}
      {showAllCluesModal && selectedGrid && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center p-3 animate-in fade-in">
          <div className="bg-[#fffdf9] border-2 border-[#b45309]/40 rounded-2xl p-4 max-w-md w-full max-h-[85vh] flex flex-col justify-between gap-3 shadow-2xl">
            <div className="flex items-center justify-between border-b border-stone-200 pb-2">
              <h3 className="font-black text-sm text-amber-950 uppercase flex items-center gap-1.5 font-cinzel">
                <span>📋</span>
                <span>Définitions ({solvedWordsCount}/{selectedGrid.words.length})</span>
              </h3>
              <button
                type="button"
                onClick={() => setShowAllCluesModal(false)}
                className="p-1.5 text-stone-500 hover:text-stone-900 bg-stone-100 rounded-lg text-xs cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-2 pr-1">
              {selectedGrid.words.map((w, idx) => {
                const isSelected = activeWordId === w.id;
                const solved = isWordSolved(w);
                const isRevealed = !selectedGrid.isEvolving || idx < revealedCount;

                return (
                  <button
                    key={w.id}
                    type="button"
                    onClick={() => {
                      if (!isRevealed) {
                        setManualRevealedCount((prev) => Math.max(prev, idx + 1));
                      }
                      handleSelectWord(w);
                      setShowAllCluesModal(false);
                    }}
                    className={`w-full text-left p-3 rounded-xl border-2 transition-all flex items-start justify-between gap-2 cursor-pointer ${
                      isSelected
                        ? 'bg-amber-100 border-amber-600 shadow-xs ring-2 ring-amber-500/30'
                        : solved
                        ? 'bg-emerald-50 border-emerald-300 text-stone-700'
                        : isRevealed
                        ? 'bg-[#fffdf9] border-stone-200 text-stone-800 hover:border-amber-400'
                        : 'bg-stone-100 border-stone-200 text-stone-400'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="font-black text-amber-800 text-xs shrink-0 font-mono">
                        {w.number}.
                      </span>
                      <div className="text-xs">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`font-semibold mr-1.5 ${isRevealed ? 'text-stone-900' : 'text-stone-500'}`}>
                            {w.isPhrase ? '💬' : w.direction === 'across' ? '➡️' : '⬇️'} {w.fr}
                          </span>
                          {!isRevealed && (
                            <span className="text-[9px] text-amber-900 bg-amber-100 border border-amber-300 px-1.5 py-0.5 rounded font-mono font-bold">
                              🔒 À débloquer
                            </span>
                          )}
                        </div>
                        <span className="text-[10px] text-stone-500 font-mono block mt-0.5">
                          ({getWordCells(w).filter((c) => !c.isSpace).length} lettres{w.isPhrase ? ' • Phrase' : ''})
                          {w.attachType === 'intersect' && ' • Croisé'}
                          {w.attachType === 'below' && ' • Rattaché'}
                        </span>
                      </div>
                    </div>

                    {solved ? (
                      <span className="text-xs font-bold text-emerald-600 shrink-0">✓</span>
                    ) : !isRevealed ? (
                      <span className="text-[10px] text-amber-800 font-bold shrink-0 underline">
                        Débloquer
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => setShowAllCluesModal(false)}
              className="w-full py-2.5 bg-stone-100 hover:bg-stone-200 text-stone-800 font-bold text-xs uppercase tracking-wider rounded-xl border border-stone-300 transition-all cursor-pointer shrink-0"
            >
              Retour à la Grille
            </button>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 5. VICTORY CELEBRATION MODAL                                              */}
      {/* ========================================================================= */}
      {showVictoryModal && selectedGrid && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-[#fffdf9] border-2 border-amber-500 rounded-2xl p-6 max-w-sm w-full text-center space-y-3.5 shadow-2xl animate-in zoom-in-95">
            <div className="text-4xl animate-bounce">🏆</div>
            <h2 className="text-lg sm:text-xl font-black text-amber-950 uppercase tracking-wider font-cinzel">
              Grille Complétée !
            </h2>
            <p className="text-xs text-stone-700 leading-relaxed">
              Félicitations ! Vous avez résolu l'intégralité de la grille{' '}
              <strong className="text-amber-950">« {selectedGrid.title} »</strong> !
            </p>

            <div className="bg-amber-50 border-2 border-amber-300 p-3 rounded-xl flex items-center justify-around font-mono">
              <div>
                <span className="text-[9px] text-stone-500 block uppercase font-bold">Statut</span>
                <span className="text-sm font-black text-amber-900">100% Réussi</span>
              </div>
              <div className="h-6 w-px bg-amber-300" />
              <div>
                <span className="text-[9px] text-stone-500 block uppercase font-bold">Mots Résolus</span>
                <span className="text-sm font-black text-emerald-700">
                  {selectedGrid.words.length} / {selectedGrid.words.length}
                </span>
              </div>
            </div>

            {(() => {
              const curGridIdx = challengeGrids ? challengeGrids.findIndex((g) => g.id === selectedGrid.id) : -1;
              const nextGrid =
                curGridIdx >= 0 && challengeGrids && curGridIdx < challengeGrids.length - 1
                  ? challengeGrids[curGridIdx + 1]
                  : null;

              return (
                <div className="flex flex-col gap-2 pt-1">
                  {nextGrid && (
                    <button
                      type="button"
                      onClick={() => {
                        handleSelectGrid(nextGrid);
                      }}
                      className="w-full py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 text-stone-950 font-black text-xs uppercase tracking-wider rounded-xl shadow-xs transition-all cursor-pointer flex items-center justify-center gap-1.5"
                    >
                      <span>Grille Suivante ({curGridIdx + 2}/{challengeGrids?.length})</span>
                      <span>▶</span>
                    </button>
                  )}

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        playSound('click');
                        setShowVictoryModal(false);
                      }}
                      className="flex-1 py-2.5 bg-stone-100 hover:bg-stone-200 text-stone-800 font-bold text-xs uppercase tracking-wider rounded-xl border border-stone-300 transition-all cursor-pointer"
                    >
                      Revoir
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        playSound('click');
                        setShowVictoryModal(false);
                        if (challengeGrid || challengeGrids) {
                          onBack();
                        } else {
                          setSelectedGrid(null);
                        }
                      }}
                      className="flex-1 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 text-stone-950 font-black text-xs uppercase tracking-wider rounded-xl shadow-xs transition-all cursor-pointer border border-amber-600"
                    >
                      {challengeGrid || challengeGrids ? 'Défis 🎯' : 'Grilles 🧩'}
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Bottom Rug Runner Border */}
      <BerberRugBorder inverted={true} className="shrink-0 mt-auto" />
    </div>
  );
};
